import "@fontsource-variable/montserrat";
import "@fontsource/krona-one";
import { Buffer } from "buffer";
import { HeroUIProvider } from "@heroui/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import ReactDOM from "react-dom/client";
import { BrowserRouter, type NavigateOptions, Route, Routes, useHref, useNavigate } from "react-router";
import { WagmiProvider } from "wagmi";
import "./index.css";
import Root from "./layouts/root.tsx";
import { initializeConfig } from "./lib/config-loader.ts";
import { getQueryClient } from "./lib/queryClient.ts";
import BridgeRoute from "./routes/bridge.tsx";
import ShieldingRoute from "./routes/shielding.tsx";
import TokensRoute from "./routes/tokens.tsx";
import TransactionsRoute from "./routes/transactions.tsx";
import TransfersRoute from "./routes/transfers.tsx";
import { config } from "./wagmi.ts";

declare module "@react-types/shared" {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

globalThis.Buffer = Buffer;

const queryClient = getQueryClient();

function App() {
  const navigate = useNavigate();
  return (
    <HeroUIProvider navigate={navigate} useHref={useHref}>
      <Routes>
        <Route element={<Root />}>
          <Route index element={<BridgeRoute />} />
          <Route path="transactions" element={<TransactionsRoute />} />
          <Route path="tokens" element={<TokensRoute />} />
          <Route path="transfers" element={<TransfersRoute />} />
          <Route path="shielding" element={<ShieldingRoute />} />
        </Route>
      </Routes>
    </HeroUIProvider>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

// Initialize config before rendering the app
initializeConfig("sandbox")
  .then(() => {
    ReactDOM.createRoot(rootElement).render(
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </WagmiProvider>,
    );
  })
  .catch((error) => {
    console.error("Failed to initialize config:", error);
    // Show error UI
    ReactDOM.createRoot(rootElement).render(
      <div style={{ padding: "20px", color: "red" }}>
        <h1>Failed to load configuration</h1>
        <p>{error.message}</p>
      </div>,
    );
  });
